import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  deriveYesNoString,
  StringValueYesNoChip,
} from "@/metrics/metric-display"
import { WordsPerMessageWithThresholdConfig } from "@/metrics/registry/configuration"

import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type {
  MetricRecordData,
  TargetCondition,
} from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import { BinaryMetricRecord } from "../base/BinaryMetricRecord"
import { parseResultJson } from "../helpers/parseResultJson"

type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

interface MessageResult {
  role: string
  index: number
  result: "YES" | "NO"
}

interface WordsPerMessageThresholdResultJson {
  explanation?: string
  threshold?: number
  operator?: string
  result?: string
  message_results?: MessageResult[]
  agent_word_counts?: number[]
}

export class WordsPerMessageWithThresholdMetricRecord extends BinaryMetricRecord {
  documentation =
    "Checks whether each agent message satisfies (word_count operator threshold). Result is YES if all messages pass, NO otherwise."

  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(metricOutput: {
    result_json?: Record<string, unknown> | null
  }): boolean {
    const resultJson = metricOutput.result_json
    if (resultJson?.threshold == null && resultJson?.operator == null) {
      return true
    }
    if (
      (resultJson?.threshold ?? null) !==
      (this.metric_metadata?.threshold ?? null)
    ) {
      return false
    }
    if (
      (resultJson?.operator ?? null) !==
      (this.metric_metadata?.operator ?? null)
    ) {
      return false
    }
    return true
  }

  getVersionDiff(metricOutput: {
    result_json?: Record<string, unknown> | null
  }): Array<{ field: string; current: string; previous: string }> {
    const diffs: Array<{ field: string; current: string; previous: string }> =
      []
    const resultJson = metricOutput.result_json
    if (
      resultJson?.threshold != null &&
      (resultJson.threshold ?? null) !==
        (this.metric_metadata?.threshold ?? null)
    ) {
      diffs.push({
        field: "Threshold",
        current: String(this.metric_metadata?.threshold ?? ""),
        previous: stringifyUnknownValue(resultJson.threshold),
      })
    }
    if (
      resultJson?.operator != null &&
      (resultJson.operator ?? null) !== (this.metric_metadata?.operator ?? null)
    ) {
      diffs.push({
        field: "Operator",
        current: String(this.metric_metadata?.operator ?? ""),
        previous: stringifyUnknownValue(resultJson.operator),
      })
    }
    return diffs
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    if (deriveYesNoString(mo) === ANNOTATION_VALUE.UNKNOWN) {
      return null
    }
    const effectiveTargetCondition: TargetCondition | undefined = this
      .invertYesNoColors
      ? { target_values: [ANNOTATION_VALUE.NO], comparison_operator: "in" }
      : undefined
    return (
      <StringValueYesNoChip
        mo={mo}
        targetCondition={effectiveTargetCondition}
        metric={this.invertYesNoColors ? undefined : this}
      />
    )
  }

  _renderDeepDive(
    mo: RunMetricOutput,
    _testCase?: BaseTestCaseRecord,
    dispatchMessageSelect?: (messageIndex: number | null) => void,
  ): React.ReactNode {
    const parsed = parseResultJson<WordsPerMessageThresholdResultJson>(mo) ?? {}
    const { explanation, threshold, operator } = parsed
    const result = parsed.result ?? mo.string_value ?? undefined
    const message_results = resolveMessageResults(mo, parsed)

    const isClickable = dispatchMessageSelect != null

    return (
      <div className="space-y-3">
        {explanation != null && (
          <p className="text-sm text-coval-text">{explanation}</p>
        )}
        <div className="text-sm space-y-1">
          {threshold != null && (
            <p>
              <span className="font-medium text-coval-text-muted">
                Threshold:
              </span>{" "}
              {threshold}
            </p>
          )}
          {operator != null && (
            <p>
              <span className="font-medium text-coval-text-muted">
                Operator:
              </span>{" "}
              {operator}
            </p>
          )}
          {result != null && (
            <p>
              <span className="font-medium text-coval-text-muted">Result:</span>{" "}
              {result === "YES" ? "Yes" : result === "NO" ? "No" : "N/A"}
            </p>
          )}
        </div>
        {message_results.length > 0 && (
          <div className="border border-divider rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-1 border-b border-divider">
                  <th className="text-left font-medium text-coval-text-muted px-3 py-2">
                    Message
                  </th>
                  <th className="text-left font-medium text-coval-text-muted px-3 py-2">
                    In threshold
                  </th>
                </tr>
              </thead>
              <tbody>
                {message_results.map((item) => {
                  const isAgent = item.role === "agent"
                  const inThreshold = item.result === "YES"
                  return (
                    <tr
                      key={item.index}
                      role={isClickable ? "button" : undefined}
                      onClick={() =>
                        isClickable && dispatchMessageSelect(item.index)
                      }
                      className={`border-b border-divider last:border-b-0 ${
                        isClickable
                          ? "cursor-pointer hover:bg-surface-1 transition-colors"
                          : ""
                      } ${!isAgent ? "bg-surface-1 text-coval-text-muted" : ""} ${isAgent && !inThreshold ? "bg-coval-danger-surface" : ""}`}
                    >
                      <td className="px-3 py-2">
                        <span
                          className={
                            !isAgent
                              ? "text-coval-text-muted"
                              : "text-coval-text"
                          }
                        >
                          Message {item.index + 1}
                          {!isAgent && (
                            <span className="ml-1 text-coval-text-muted italic">
                              (not included in calculation)
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={!isAgent ? "opacity-50" : undefined}>
                          <StringValueYesNoChip value={item.result} />
                        </span>
                        {!isAgent && (
                          <span className="ml-1 text-coval-text-muted italic text-xs">
                            (not included in calculation)
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    return (
      <WordsPerMessageWithThresholdConfig
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    )
  }
}

// Reconstruct per-message rows from subvalues; fall back to legacy result_json.
function resolveMessageResults(
  mo: RunMetricOutput,
  parsed: WordsPerMessageThresholdResultJson,
): MessageResult[] {
  const subvalues = mo.subvalues_by_timestamp ?? []
  if (subvalues.length > 0) {
    return subvalues
      .filter((sv) => typeof sv.message_index === "number")
      .map((sv) => ({
        role: sv.role ?? "agent",
        index: sv.message_index as number,
        result: sv.string_value === ANNOTATION_VALUE.YES ? "YES" : "NO",
      }))
  }
  return parsed.message_results ?? []
}
